import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { InstitutionalFlowChart } from "@/cockpit/shadow/InstitutionalFlowChart";
import { InsiderScatterPlot } from "@/cockpit/shadow/InsiderScatterPlot";
import { PoliticalFlowTimeline } from "@/cockpit/shadow/PoliticalFlowTimeline";
import { DilutionPressureLine } from "@/cockpit/shadow/DilutionPressureLine";
import { SectorHeatmapGrid } from "@/cockpit/SectorHeatmapGrid";
import { CockpitDrawer, RawTable } from "@/cockpit/CockpitDrawer";
import { compactShares, compactUsd } from "@/cockpit/formatCockpit";
import { CockpitPanel } from "@/cockpit/CockpitPanel";

export function ShadowMoneyCockpit({ data }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [drawer, setDrawer] = useState(null);
  const shadow = data?.shadow;
  const open = isAdmin ? (key) => () => setDrawer(key) : () => undefined;
  const drawers = useMemo(() => ({
    inst: {
      title: "13F top holders",
      description: "House-feed 13F snapshot. Lagged holdings, not a transaction tape. One vendor page summarized to top holders.",
      columns: [
        { key: "investorName", label: "Holder" },
        { key: "positionValueUsd", label: "Pos. value (ticker)", align: "right", render: (r) => compactUsd(r.positionValueUsd) },
        { key: "shares", label: "Shares", align: "right", render: (r) => compactShares(r.shares) },
      ],
      rows: shadow?.institutionalHolders || [],
    },
    insider: {
      title: "Form 4 raw prints",
      description: "SEC Form 4 via house feed. Delayed. Not current holdings.",
      columns: [
        { key: "date", label: "Date" },
        { key: "insiderName", label: "Insider" },
        { key: "type", label: "Type" },
        { key: "shares", label: "Shares", align: "right", render: (r) => compactShares(r.shares) },
        { key: "valueUsd", label: "Value", align: "right", render: (r) => compactUsd(r.valueUsd) },
      ],
      rows: shadow?.insiderActivity || [],
    },
    ptr: {
      title: "PTR raw rows",
      description: "Per-filing STOCK Act brackets. Default panel groups these by politician.",
      columns: [
        { key: "politicianName", label: "Politician" },
        { key: "chamber", label: "Chamber" },
        { key: "type", label: "Side" },
        { key: "amountBracket", label: "Amount" },
        { key: "tradeDate", label: "Trade" },
        { key: "disclosureDate", label: "Disclosed" },
        { key: "delayDays", label: "Delay d", align: "right" },
      ],
      rows: shadow?.politicalTrades || [],
    },
    dilution: {
      title: "Form 144 proposed sales",
      description: "House-feed Form 144 notices. Proposed, not confirmed sold.",
      columns: [
        { key: "date", label: "Date" },
        { key: "seller", label: "Seller" },
        { key: "shares", label: "Shares", align: "right", render: (r) => compactShares(r.shares) },
        { key: "marketValueUsd", label: "Value", align: "right", render: (r) => compactUsd(r.marketValueUsd) },
      ],
      rows: shadow?.proposedSales || [],
    },
  }), [shadow]);

  const active = drawer ? drawers[drawer] : null;

  return (
    <div className="space-y-3" data-testid="shadow-money-cockpit">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <InstitutionalFlowChart
          flows={shadow?.institutionalFlows}
          holders={shadow?.institutionalHolders}
          note={shadow?.institutionsNote}
          onOpenRaw={isAdmin ? open("inst") : undefined}
        />
        <InsiderScatterPlot
          activity={shadow?.insiderActivity}
          buyCount30d={shadow?.buyCount30d}
          sellCount30d={shadow?.sellCount30d}
          onOpenRaw={isAdmin ? open("insider") : undefined}
        />
        <PoliticalFlowTimeline
          timeline={shadow?.politicalTimeline}
          trades={shadow?.politicalTrades}
          onOpenRaw={isAdmin ? open("ptr") : undefined}
        />
        <DilutionPressureLine
          sales={shadow?.proposedSales}
          dataSourceStatus={shadow?.dilutionDataSourceStatus}
          onOpenRaw={isAdmin ? open("dilution") : undefined}
        />
      </div>
      {(shadow?.sectorPoliticalFlow || []).length > 0 ? (
      <CockpitPanel
        title="Congressional flow by GICS sector"
        hint="Bracket-midpoint estimates lumped by sector. Not in the FDN house-feed snapshot unless a later source fills it."
        testid="shadow-sector-heatmap"
      >
        <SectorHeatmapGrid
          items={shadow?.sectorPoliticalFlow}
          testid="shadow-sector-heatmap-grid"
          extraLine={(s) => (
            <div>D {compactUsd(s.democratNetUsdEstimate)} · R {compactUsd(s.republicanNetUsdEstimate)}</div>
          )}
        />
      </CockpitPanel>
      ) : null}
      <CockpitDrawer
        open={!!active}
        onOpenChange={(v) => { if (!v) setDrawer(null); }}
        title={active?.title}
        description={active?.description}
        testid="shadow-raw-drawer"
      >
        {active ? <RawTable columns={active.columns} rows={active.rows} /> : null}
      </CockpitDrawer>
    </div>
  );
}
